import moment from 'moment';

export type CalendarViewName = 'month' | 'week' | 'work_week' | 'day' | 'agenda';

export interface CalendarDateRange {
  /** Inclusive ISO start of the visible range. */
  start: string;
  /** Inclusive ISO end of the visible range. */
  end: string;
}

/**
 * §3 — the date range a calendar view currently SHOWS, used to lazy-load delivered (successfully-
 * sent) messages scoped to what's on screen rather than a global tail.
 *
 * - month / agenda: the full month GRID — the weeks that span the month (react-big-calendar renders
 *   leading/trailing days of adjacent months), so events in those visible cells are covered.
 * - week / work_week: the week containing `currentDate`.
 * - day: that day.
 *
 * Pure + framework-free (moment only) so it is unit-testable without the calendar harness.
 * Week boundaries follow moment's locale (Sun/Mon start) — the same basis the grid renders on.
 */
export function getCalendarVisibleRange(
  currentDate: Date | string | number,
  view: CalendarViewName,
): CalendarDateRange {
  const d = moment(currentDate);
  if (!d.isValid()) {
    // Fail safe: an unparseable date yields a benign single-day range (no huge fetch).
    const now = moment();
    return { start: now.clone().startOf('day').toISOString(), end: now.clone().endOf('day').toISOString() };
  }

  if (view === 'day') {
    return { start: d.clone().startOf('day').toISOString(), end: d.clone().endOf('day').toISOString() };
  }
  if (view === 'week' || view === 'work_week') {
    return { start: d.clone().startOf('week').toISOString(), end: d.clone().endOf('week').toISOString() };
  }
  // month + agenda: the visible month grid (weeks spanning the month).
  return {
    start: d.clone().startOf('month').startOf('week').toISOString(),
    end: d.clone().endOf('month').endOf('week').toISOString(),
  };
}
